import { inject, Injectable } from '@angular/core';
import {
  CardIssuerFeeV2,
  DreampayPaymentV2,
  DreampayPaymentResponseV2,
  EstablishmentV2,
  PaymentLinkV2,
  SettingV2,
  CurrencySettingsResponseV2,
} from 'api-client';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { LogService } from '../../core/services/log.service';
import { NewNotification } from '../models/notification.class';
import { Transaction } from '../models/transaction.class';

/**
 * Global state interface for the application
 */
export interface StoreState {
  // Common data
  linkId?: string; // ID used to fetch payment link data
  paymentLink?: PaymentLinkV2;
  establishment?: EstablishmentV2;

  // Payment processing data
  transaction?: Transaction;
  dreampayPayment?: DreampayPaymentV2;
  paymentResponse?: DreampayPaymentResponseV2;

  // UI state
  finishStepMessage?: string;
  isPaymentError?: boolean;
  showPixStep?: boolean;
  isCredit?: boolean;

  // Payment details
  installmentValueBRL?: number;

  // Card details
  cardImgPath?: string;
  cardIssuerFees?: CardIssuerFeeV2;
  cardType?: string;

  // Payment transaction details
  paymentID?: string; // Used in Transaction class (uppercase ID)
  PaymentId?: string; // Used in response models (uppercase P and I)
  tid?: string;
  authorizationCode?: string;
  proofSale?: string;
  paymentAccountReference?: string;

  // Payment status
  paid?: boolean;
  canceled?: boolean;
  cancelRequested?: boolean;
  cancelRequestDate?: string;
  canceledDate?: string;
  canceledTID?: string;
  canceledAuthorizationCode?: string;
  canceledProofSale?: string;

  // Transaction response
  returnCode?: string;
  returnMessage?: string;

  // Financial details
  exchangeCostBRL?: number;
  cardProcessingComission?: number;
  cardProcessingCostBRL?: number;
  antecipationCostBRL?: number;
  operationalMarginBRL?: number;

  // Settings and configuration
  settings?: SettingV2 | CurrencySettingsResponseV2;
  currency?: 'EUR' | 'USD' | 'BRL';
  exchangeRate?: number;
  currencySettingsLoaded?: boolean; // Flag to indicate settings have been loaded

  // Notifications
  failedPayNotification?: NewNotification;

  // Payment options
  creditOnly?: boolean;
  minimumInstallments?: number;
  defaultInstallments?: number;

  // Checkout specific data
  customerName?: string;
  customerEmail?: string;
  customerAddress?: string;
  customerCity?: string;
  customerZipCode?: string;
  customerPhoneNumber?: string;
  customerCPFCNPJ?: string;

  // Form data
  cardNumber?: string;
  cardHolder?: string;
  cpf?: string;
  expirationDate?: string;
  securityCode?: string;
  installments?: number;

  // GRERJ specific data
  pdfUrl?: string;
}

/**
 * Global store service for the application
 * Provides a simple yet robust state management solution
 */
@Injectable({
  providedIn: 'root',
})
export class StoreService {
  private logger = inject(LogService);

  // Initial state
  private initialState: StoreState = {
    showPixStep: false,
    isCredit: true,
    creditOnly: false,
    minimumInstallments: 1,
    defaultInstallments: 12,
    installments: 1,
  };

  // BehaviorSubject to hold the current state
  private state$ = new BehaviorSubject<StoreState>(this.initialState);

  /**
   * Get the current state value
   */
  get currentState(): StoreState {
    return this.state$.getValue();
  }

  /**
   * Get the state as an observable
   */
  get state(): Observable<StoreState> {
    return this.state$.asObservable();
  }

  /**
   * Select a specific part of the state
   * @param selector Function to select part of the state
   */
  select<T>(selector: (state: StoreState) => T): Observable<T> {
    return this.state$.pipe(map(selector));
  }

  /**
   * Update the state
   * @param newState Partial state to merge with current state
   */
  updateState(newState: Partial<StoreState>): void {
    this.state$.next({
      ...this.currentState,
      ...newState,
    });
  }

  /**
   * Reset the state to initial values
   */
  resetState(): void {
    this.state$.next(this.initialState);
  }

  /**
   * Reset specific parts of the state
   * @param keys Keys to reset to initial values
   */
  resetStateProperties(...keys: (keyof StoreState)[]): void {
    this.updateState(Object.fromEntries(keys.map((key) => [key, undefined])));
  }

  /**
   * Clear sensitive card data
   */
  clearSensitiveData(): void {
    this.updateState({
      cardNumber: undefined,
      cardHolder: undefined,
      cpf: undefined,
      expirationDate: undefined,
      securityCode: undefined,
    });
  }

  /**
   * Log state information to the console
   * @param message Optional message to include with the log
   * @param properties Optional specific properties to log (logs all state if not specified)
   */
  logState(message?: string, ...properties: (keyof StoreState)[]): void {
    const state = this.currentState;

    if (properties && properties.length > 0) {
      // Log only specified properties
      const selectedState = properties.reduce<Record<string, unknown>>((result, prop) => {
        result[prop as string] = state[prop];
        return result;
      }, {});
      this.logger.debug(message || 'Current State:', 'StoreService.logState', selectedState);
    } else {
      // Log entire state
      this.logger.debug(message || 'Current State:', 'StoreService.logState', state);
    }
  }

  // Common data

  /**
   * Set the link ID
   * @param linkId Link ID
   */
  setLinkId(linkId: string): void {
    this.updateState({ linkId });
  }

  /**
   * Get the link ID
   */
  getLinkId(): string | undefined {
    return this.currentState.linkId;
  }

  // Payment Link and Establishment

  /**
   * Set the payment link
   * @param paymentLink Payment link data
   */
  setPaymentLink(paymentLink: PaymentLinkV2): void {
    this.updateState({
      paymentLink,
      customerName: paymentLink.customerName ?? undefined,
      customerEmail: paymentLink.customerEmail ?? undefined,
      pdfUrl: paymentLink.pdfUrl ?? undefined,
    });
  }

  /**
   * Set the establishment
   * @param establishment Establishment data
   */
  setEstablishment(establishment: EstablishmentV2): void {
    this.updateState({
      establishment,
    });
  }

  /**
   * Get the payment link
   */
  getPaymentLink(): PaymentLinkV2 | undefined {
    return this.currentState.paymentLink;
  }

  /**
   * Get the establishment
   */
  getEstablishment(): EstablishmentV2 | undefined {
    return this.currentState.establishment;
  }

  // Payment Processing

  /**
   * Set transaction data
   * @param transaction Transaction data
   */
  setTransaction(transaction: Transaction): void {
    this.updateState({ transaction });
  }

  /**
   * Update transaction data
   * @param transactionData Partial transaction data
   */
  updateTransaction(transactionData: Partial<Transaction>): void {
    const currentTransaction = this.currentState.transaction || {};

    this.updateState({
      transaction: {
        ...currentTransaction,
        ...transactionData,
      },
    });
  }

  /**
   * Set Dreampay payment data
   * @param dreampayPayment Payment data
   */
  setDreampayPayment(dreampayPayment: DreampayPaymentV2): void {
    this.updateState({ dreampayPayment });
  }

  /**
   * Set payment response
   * @param paymentResponse Payment response data
   */
  setPaymentResponse(paymentResponse: DreampayPaymentResponseV2): void {
    this.updateState({ paymentResponse });
  }

  // UI State

  /**
   * Set finish step message
   * @param message Message to display
   * @param isError Whether this is an error message
   */
  setFinishStepMessage(message: string, isError = false): void {
    this.updateState({ finishStepMessage: message, isPaymentError: isError });
  }

  /**
   * Get finish step message
   */
  getFinishStepMessage(): string | undefined {
    return this.currentState.finishStepMessage;
  }

  /**
   * Get payment error state
   */
  getIsPaymentError(): boolean {
    return this.currentState.isPaymentError ?? false;
  }

  /**
   * Toggle PIX step visibility
   * @param show Whether to show PIX step
   */
  togglePixStep(show: boolean): void {
    this.updateState({ showPixStep: show });
  }

  /**
   * Set PIX step visibility
   * @param show Whether to show PIX step
   */
  setShowPixStep(show: boolean): void {
    this.updateState({ showPixStep: show });
  }

  /**
   * Get PIX step visibility
   */
  getShowPixStep(): boolean | undefined {
    return this.currentState.showPixStep;
  }

  /**
   * Toggle credit/debit payment method
   * @param isCredit Whether credit is selected
   */
  toggleCreditDebit(isCredit: boolean): void {
    this.updateState({ isCredit });
  }

  // Card Details

  /**
   * Set card image path
   * @param cardImgPath Path to card image
   */
  setCardImagePath(cardImgPath: string): void {
    this.updateState({ cardImgPath });
  }

  /**
   * Set card issuer fees
   * @param cardIssuerFees Card issuer fees
   */
  setCardIssuerFees(cardIssuerFees: CardIssuerFeeV2): void {
    this.updateState({ cardIssuerFees });
  }

  // Settings

  /**
   * Set application settings
   * @param settings Application settings
   */
  setSettings(settings: SettingV2 | CurrencySettingsResponseV2): void {
    this.updateState({ settings });
  }

  /**
   * Set currency settings (currency type + settings + exchange rate)
   * Used by CurrencySettingsService after loading settings with retry logic
   * @param currency Currency type ('EUR', 'USD', 'BRL')
   * @param settings Currency settings (may be undefined for BRL)
   */
  setCurrencySettings(
    currency: 'EUR' | 'USD' | 'BRL',
    settings?: SettingV2 | CurrencySettingsResponseV2,
  ): void {
    this.updateState({
      currency,
      settings,
      exchangeRate: settings?.oficialExchangeRate ?? undefined,
      currencySettingsLoaded: true,
    });
  }

  /**
   * Get currency settings loaded flag
   */
  getCurrencySettingsLoaded(): boolean {
    return this.currentState.currencySettingsLoaded ?? false;
  }

  /**
   * Get current currency
   */
  getCurrency(): 'EUR' | 'USD' | 'BRL' | undefined {
    return this.currentState.currency;
  }

  /**
   * Set exchange rate
   * @param exchangeRate Exchange rate value
   */
  setExchangeRate(exchangeRate: number): void {
    this.updateState({ exchangeRate });
  }

  // Notifications

  /**
   * Set failed payment notification
   * @param notification Notification data
   */
  setFailedPayNotification(notification: NewNotification): void {
    this.updateState({ failedPayNotification: notification });
  }

  // Payment Options

  /**
   * Set credit only flag
   * @param creditOnly Whether only credit is allowed
   */
  setCreditOnly(creditOnly: boolean): void {
    this.updateState({ creditOnly });
  }

  /**
   * Set minimum installments
   * @param minimumInstallments Minimum number of installments
   */
  setMinimumInstallments(minimumInstallments: number): void {
    this.updateState({ minimumInstallments });
  }

  /**
   * Set default installments
   * @param defaultInstallments Default number of installments
   */
  setDefaultInstallments(defaultInstallments: number): void {
    this.updateState({ defaultInstallments });
  }

  /**
   * Set number of installments
   * @param installments Number of installments
   */
  setInstallments(installments: number): void {
    this.updateState({ installments });
  }

  // Customer Data

  /**
   * Set customer data
   * @param customerData Customer data
   */
  setCustomerData(customerData: {
    customerName?: string;
    customerEmail?: string;
    customerAddress?: string;
    customerCity?: string;
    customerZipCode?: string;
    customerPhoneNumber?: string;
    customerCPFCNPJ?: string;
  }): void {
    this.updateState(customerData);
  }

  // Form Data

  /**
   * Set card form data
   * @param cardData Card form data
   */
  setCardFormData(cardData: {
    cardNumber?: string;
    cardHolder?: string;
    cpf?: string;
    expirationDate?: string;
    securityCode?: string;
  }): void {
    this.updateState(cardData);
  }
}
